/**
 * キャプション生成オーケストレーター (純粋関数)。
 *
 * 入力: 80 枚分の分類結果 + 車両メタデータ + ヒーロー index
 * 出力: 80 件の CaptionEntry (filename + caption + categoryId + patternIdx)
 *
 * 並び順: ヒーロー写真が必ず先頭 (01_hero.png)、それ以外は入力 index の昇順。
 */

import { sequentialFileName } from '../image';
import type { CategoryId, ClassificationResult, VehicleMetadata } from '@shared/types';

import { renderCaption } from './renderer';
import { emptyState, selectTemplate, type SelectionState } from './selector';

export interface CaptionEntry {
  readonly filename: string;
  readonly caption: string;
  readonly categoryId: CategoryId;
  readonly patternIdx: number;
}

export interface GenerateCaptionsParams {
  readonly classifications: readonly ClassificationResult[];
  readonly heroIndex: number;
  readonly metadata: VehicleMetadata;
  readonly random?: () => number;
}

export function generateCaptions(params: GenerateCaptionsParams): readonly CaptionEntry[] {
  const { classifications, heroIndex, metadata } = params;
  const n = classifications.length;
  if (n === 0) return [];
  if (!Number.isInteger(heroIndex) || heroIndex < 0 || heroIndex >= n) {
    throw new RangeError(`heroIndex out of range: ${heroIndex} (n=${n})`);
  }

  const order = reorderWithHeroFirst(n, heroIndex);
  let state: SelectionState = emptyState();
  const entries: CaptionEntry[] = [];

  for (let outputIdx = 0; outputIdx < n; outputIdx++) {
    const inputIdx = order[outputIdx];
    if (inputIdx === undefined) continue;
    const isHero = outputIdx === 0;
    const categoryId = isHero ? (1 as CategoryId) : classifications[inputIdx]?.categoryId;
    if (!categoryId) continue;
    const selection = selectTemplate(categoryId, state, params.random);
    state = selection.nextState;
    entries.push({
      filename: sequentialFileName(outputIdx, isHero),
      caption: renderCaption(selection.template, metadata),
      categoryId,
      patternIdx: selection.patternIdx,
    });
  }

  return entries;
}

function reorderWithHeroFirst(n: number, heroIndex: number): readonly number[] {
  const result = [heroIndex];
  for (let i = 0; i < n; i++) {
    if (i !== heroIndex) result.push(i);
  }
  return result;
}
